import { Component, effect, inject } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  BasicInfoDTO,
  Trip,
  TripBasicInfo,
} from 'src/app/trip-calculator/models/trip';
import { TripCreationModalService } from 'src/app/trip-calculator/services/trip-creation-modal.service';
import {
  CreateTripResponse,
  TripService,
} from 'src/app/trip-calculator/services/trip.service';
import { InputText } from 'primeng/inputtext';
import { ErrorComponent } from '../../error/error.component';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { SelectCountryComponent } from '../../custom-input/select-country/select-country.component';
import { CompanyClientComponent } from '../company-client/company-client.component';
import { CompanyClientService } from 'src/app/shared/services/company-client.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LockedButtonComponent } from 'src/app/shared/components/locked-button/locked-button.component';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';

@Component({
  selector: 'app-trip-basic-info-form',
  imports: [
    ButtonModule,
    TranslocoPipe,
    ReactiveFormsModule,
    InputText,
    ErrorComponent,
    InputNumber,
    DatePicker,
    SelectCountryComponent,
    CompanyClientComponent,
    LockedButtonComponent,
  ],
  templateUrl: './trip-basic-info-form.component.html',
})
export class TripBasicInfoFormComponent {
  tripCreationModalService = inject(TripCreationModalService);
  tripService = inject(TripService);
  companyClientService = inject(CompanyClientService);
  notificationService = inject(NotificationService);
  translocoService = inject(TranslocoService);
  fb = inject(NonNullableFormBuilder);
  packLimitationService = inject(PackLimitationsService);
  tripForm = this.fb.group({
    title: ['', Validators.required],
    countryCode: ['', Validators.required],
    days: [1, [Validators.required, Validators.min(1)]],
    travellers: [1, [Validators.required, Validators.min(1)]],
    price: this.fb.control<number | null>(null),
    startDate: this.fb.control<Date | null>(null),
    companyClientsId: this.fb.control<string[]>([]),
  });
  loading = false;
  limitedFeature = LimitedFeatures.Client

  constructor() {
    effect(() => {
      const trip = this.tripService.trip();
      if (trip && this.tripCreationModalService.mode() === 'edit') {
        this.tripForm.patchValue({
          title: trip.title,
          countryCode: trip.countryCode,
          days: trip.days,
          travellers: trip.travellers,
          price: trip.price ?? null,
          startDate: trip.startDate ? new Date(trip.startDate) : null,
          companyClientsId: trip.companyClients.map(
            (companyClients) => companyClients.id,
          ),
        });
        // An AI-extracted trip can come without a country: surface the
        // required error right away instead of waiting for the user to touch it.
        if (!trip.countryCode) {
          this.tripForm.controls.countryCode.markAsTouched();
        }
      }
    });
  }

  createTrip() {
    if (this.tripForm.valid) {
      this.loading = true;
      const trip: TripBasicInfo = this.patchValue(this.tripForm.getRawValue());
      this.tripService.createTrip(trip).subscribe({
        next: (res: CreateTripResponse) => {
          this.loading = false;
          res.trip.id && this.tripService.navigateToTripDashboard(res.trip.id);
          this.tripCreationModalService.close();
        },
        error: async (err) => {
          console.error('error creating trip', err);
          const errorMsg = await firstValueFrom(
            this.translocoService.selectTranslate('calculator.errorSaving'),
          );
          this.notificationService.error(errorMsg);
          this.loading = false;
        },
      });
    }
  }
  editTrip() {
    if (this.tripForm.valid) {
      this.loading = true;
      const basicInfo: BasicInfoDTO = this.tripForm.getRawValue();
      const trip = this.tripService.trip();
      if (!trip) {
        return;
      }
      const updatedTrip: Trip = this.patchValue(basicInfo, trip);
      this.tripService.updateAsDraft(updatedTrip).subscribe(() => {
        this.loading = false;
        this.tripCreationModalService.close();
      });
    }
  }
  patchValue(basicInfo: BasicInfoDTO): TripBasicInfo;
  patchValue(basicInfo: BasicInfoDTO, trip: Trip): Trip;
  patchValue(basicInfo: BasicInfoDTO, trip?: Trip): TripBasicInfo | Trip {
    const patched = {
      ...trip,
      title: basicInfo.title,
      countryCode: basicInfo.countryCode,
      days: basicInfo.days,
      travellers: basicInfo.travellers,
      price: basicInfo.price ?? undefined,
      startDate: basicInfo.startDate ?? undefined,
      companyClients: this.companyClientService.getCompanyClientsFromIds(
        basicInfo.companyClientsId,
      ),
    };

    return patched as TripBasicInfo | Trip;
  }
}
